import bookingCycleMgr from "@services/bookingCycleMgr";
import { state } from "@src/interfaces/interfaces";
import { useAppSelector } from "@store/hooks";
import React from "react";
import { useNavigate } from "react-router-dom";
import { handleNewBooking, setCallAsBooking } from "../helpers";
import type { CallIdStateData } from "../types/callIdTypes";

/**
 * Custom hook to handle Five9 booking creation logic with call ID priority
 */
export const useFive9BookingLogic = (five9State: CallIdStateData) => {
	const {
		accountCode,
		agentState,
		callFrom,
		callTo,
		isOutgoingCall,
		sessionId,
		eventId,
		// NEW: Call ID properties
		callId,
		callIdResolutionStatus,
	} = five9State;

	const bookingFormLoaded = useAppSelector(
		({ state }) => state.BookingFormLoaded,
	);
	const bookingId = useAppSelector(({ ui }) => ui.BookingId);
	const savingJob = useAppSelector(({ state }) => state.SavingJob);
	const navigate = useNavigate();

	// useRef protection to prevent duplicate booking creation from re-renders
	const processedSessionsRef = React.useRef<Set<string>>(new Set());
	const processedCallIdsRef = React.useRef<Set<string>>(new Set()); // NEW: Call ID tracking
	const lastBookingCreatedRef = React.useRef<string | null>(null);
	const lastCallIdBookingCreatedRef = React.useRef<string | null>(null); // NEW: Call ID tracking
	const bookingCreationInProgressRef = React.useRef<boolean>(false);

	// Helper function to handle booking creation with session ID
	const handleBookingWithSessionId = React.useCallback(
		(sessionId: string) => {
			if (!sessionId) {
				console.warn(
					`⚠️ [FIVE9_STATE] No session ID available for booking creation`,
				);
				bookingCreationInProgressRef.current = false;
				return;
			}

			console.log(
				`🔄 [FIVE9_STATE] Processing booking with session ID: ${sessionId}`,
			);

			// Get current state values at execution time
			const currentBookingFormLoaded = state.BookingFormLoaded;
			const currentSavingJob = state.SavingJob;

			if (!currentBookingFormLoaded && !currentSavingJob) {
				// new call and on home page
				if (
					accountCode &&
					accountCode > 0 &&
					callTo &&
					!isOutgoingCall &&
					agentState === "Connected" &&
					sessionId &&
					!bookingCycleMgr.hasSessionId(sessionId)
				) {
					console.log(
						`🔄 [FIVE9_STATE] Creating new booking for incoming call: ${callFrom} → ${callTo} (Account: ${accountCode}, Session: ${sessionId})`,
					);

					// Mark session as processed
					processedSessionsRef.current.add(sessionId);
					lastBookingCreatedRef.current = sessionId;
					bookingCreationInProgressRef.current = true;

					state.BookingFormLoaded = true;
					state.CallStarted = true;
					state.FormLocked = true;

					handleNewBooking(
						navigate,
						accountCode,
						Number(callFrom),
						Number(callTo) || 0, // Pass callTo as the dialled number
						sessionId,
						eventId,
						callId, // Pass callId for API lookup
					).finally(() => {
						// Clear booking creation in progress flag
						bookingCreationInProgressRef.current = false;
					});
				} else {
					console.log(
						`🚫 [FIVE9_STATE] Booking creation blocked - conditions not met`,
					);
					bookingCreationInProgressRef.current = false;
				}
			}
			// new call when booking is opened
			else if (
				bookingFormLoaded &&
				!savingJob &&
				accountCode &&
				accountCode > 0 &&
				callTo &&
				!isOutgoingCall &&
				agentState === "Connected" &&
				sessionId &&
				!bookingCycleMgr.hasSessionId(sessionId)
			) {
				console.log(
					`🔄 [FIVE9_STATE] Handling new call while booking open: ${callFrom} → ${callTo} (Account: ${accountCode}, Session: ${sessionId})`,
				);

				// Mark session as processed
				processedSessionsRef.current.add(sessionId);
				lastBookingCreatedRef.current = sessionId;
				bookingCreationInProgressRef.current = true;

				state.CallStarted = true;
				state.FormLocked = true;

				handleNewBooking(
					navigate,
					accountCode,
					Number(callFrom),
					Number(callTo) || 0, // Pass callTo as the dialled number
					sessionId,
					eventId,
					callId, // Pass callId for API lookup
				).finally(() => {
					// Clear booking creation in progress flag
					bookingCreationInProgressRef.current = false;
				});
			} else if (agentState !== "Connected") {
				console.log(
					`🔄 [FIVE9_STATE] Agent not connected (${agentState}), clearing call state`,
				);
				state.CallStarted = false;
				state.FormLocked = false;

				// Clear useRef protection when agent disconnects
				if (lastBookingCreatedRef.current) {
					processedSessionsRef.current.delete(lastBookingCreatedRef.current);
					lastBookingCreatedRef.current = null;
				}
				if (lastCallIdBookingCreatedRef.current) {
					processedCallIdsRef.current.delete(
						lastCallIdBookingCreatedRef.current,
					);
					lastCallIdBookingCreatedRef.current = null;
				}
				bookingCreationInProgressRef.current = false;
			} else {
				bookingCreationInProgressRef.current = false;
			}
		},
		[bookingFormLoaded, savingJob, accountCode, callTo, isOutgoingCall, agentState, callFrom, navigate, eventId, callId],
	);

	// Handle new booking creation with call ID priority logic
	React.useEffect(() => {
		// Handle session cleanup when both sessionId and callId become null (call ended)
		if (!sessionId && !callId) {
			console.log(
				`🧹 [FIVE9_STATE] Both Session ID and Call ID are null - cleaning up call state`,
			);

			// Clear all booking form state to prevent recreation
			state.BookingFormLoaded = false;
			state.CallStarted = false;
			state.FormLocked = false;

			// Clear booking cycle manager state
			bookingCycleMgr.setCurrentBookingSessionId("");
			bookingCycleMgr.setCurrentBookingEventId(0);
			bookingCycleMgr.setCurrentBookingCallId(""); // NEW: Clear call ID

			// Clear useRef protection when call ends
			if (lastBookingCreatedRef.current) {
				processedSessionsRef.current.delete(lastBookingCreatedRef.current);
				lastBookingCreatedRef.current = null;
			}
			if (lastCallIdBookingCreatedRef.current) {
				processedCallIdsRef.current.delete(lastCallIdBookingCreatedRef.current);
				lastCallIdBookingCreatedRef.current = null;
			}
			bookingCreationInProgressRef.current = false;

			console.log(
				`✅ [FIVE9_STATE] Call state cleaned up - no session ID or call ID`,
			);
			return;
		}

		// Regular session ID logic - use handleBookingWithSessionId for all booking creation
		if (sessionId) {
			console.log(`🔍 [FIVE9_STATE] Processing with session ID: ${sessionId}`);

			// useRef PROTECTION: Prevent duplicate processing from re-renders
			if (processedSessionsRef.current.has(sessionId)) {
				console.log(
					`🚫 [FIVE9_STATE] Session ${sessionId} already processed by useRef - skipping re-render duplicate`,
				);
				return;
			}

			// CRITICAL: Prevent duplicate processing of the same session ID
			if (bookingCycleMgr.hasSessionId(sessionId)) {
				console.log(
					`🚫 [FIVE9_STATE] Session ${sessionId} already processed by booking manager - skipping to prevent duplicates`,
				);
				return;
			}

			// Check if booking creation is already in progress for ANY session
			if (bookingCreationInProgressRef.current) {
				console.log(
					`🚫 [FIVE9_STATE] Booking creation already in progress - skipping to prevent concurrent creation`,
				);
				return;
			}

			// Use the centralized booking creation logic
			handleBookingWithSessionId(sessionId);
		}

		// Get current state values at execution time (not from stale closure)
		const currentBookingFormLoaded = state.BookingFormLoaded;
		const currentSavingJob = state.SavingJob;

		// Debug logging to understand why bookings are being created repeatedly
		console.log(
			`🔍 [FIVE9_STATE] Booking creation useEffect triggered for session: ${sessionId}`,
			{
				currentBookingFormLoaded,
				currentSavingJob,
				accountCode,
				callTo,
				isOutgoingCall,
				agentState,
				alreadyHasSession: false, // We know it's false because we checked above
			},
		);

		if (!currentBookingFormLoaded && !currentSavingJob) {
			// new call and on home page
			if (
				accountCode &&
				accountCode > 0 &&
				callTo &&
				!isOutgoingCall &&
				agentState === "Connected" &&
				sessionId &&
				!bookingCycleMgr.hasSessionId(sessionId)
			) {
				console.log(
					`🔄 [FIVE9_STATE] Creating new booking for incoming call: ${callFrom} → ${callTo} (Account: ${accountCode}, Session: ${sessionId})`,
				);

				// Mark session as processed and set booking creation in progress
				processedSessionsRef.current.add(sessionId);
				lastBookingCreatedRef.current = sessionId;
				bookingCreationInProgressRef.current = true;

				state.BookingFormLoaded = true;
				state.CallStarted = true;
				state.FormLocked = true;

				handleNewBooking(
					navigate,
					accountCode,
					Number(callFrom),
					Number(callTo) || 0, // Pass callTo as the dialled number
					sessionId,
					eventId,
					callId, // Pass callId for API lookup
				).finally(() => {
					// Clear booking creation in progress flag
					bookingCreationInProgressRef.current = false;
				});
			} else {
				console.log(
					`🚫 [FIVE9_STATE] Booking creation blocked - conditions not met`,
				);
			}
		}
		// new call when booking is opened
		else if (
			bookingFormLoaded &&
			!savingJob &&
			accountCode &&
			accountCode > 0 &&
			callTo &&
			!isOutgoingCall &&
			agentState === "Connected" &&
			sessionId &&
			!bookingCycleMgr.hasSessionId(sessionId)
		) {
			console.log(
				`🔄 [FIVE9_STATE] Handling new call while booking open: ${callFrom} → ${callTo} (Account: ${accountCode}, Session: ${sessionId})`,
			);

			// Mark session as processed and set booking creation in progress
			processedSessionsRef.current.add(sessionId);
			lastBookingCreatedRef.current = sessionId;
			bookingCreationInProgressRef.current = true;

			state.CallStarted = true;
			state.FormLocked = true;

			handleNewBooking(
				navigate,
				accountCode,
				Number(callFrom),
				Number(callTo) || 0, // Pass callTo as the dialled number
				sessionId,
				eventId,
				callId, // Pass callId for API lookup
			).finally(() => {
				// Clear booking creation in progress flag
				bookingCreationInProgressRef.current = false;
			});
		} else if (agentState !== "Connected") {
			console.log(
				`🔄 [FIVE9_STATE] Agent not connected (${agentState}), clearing call state`,
			);
			state.CallStarted = false;
			state.FormLocked = false;

			// Clear useRef protection when agent disconnects
			if (lastBookingCreatedRef.current) {
				processedSessionsRef.current.delete(lastBookingCreatedRef.current);
				lastBookingCreatedRef.current = null;
			}
			bookingCreationInProgressRef.current = false;
		}
	}, [
		accountCode,
		agentState,
		bookingFormLoaded,
		callFrom,
		callTo,
		navigate,
		isOutgoingCall,
		sessionId,
		savingJob,
		eventId,
		callId,
		callIdResolutionStatus,
		handleBookingWithSessionId,
	]);

	// Handle booking assignment
	React.useEffect(() => {
		if (bookingId && sessionId) {
			console.log(
				`🔄 [FIVE9_STATE] Assigning session ${sessionId} to booking ${bookingId}`,
			);
			setCallAsBooking(sessionId, "booking", bookingId);
		}
	}, [bookingId, sessionId]);
};
